import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, Linkedin, Github, Send, Terminal, CheckCircle2, ShieldCheck } from 'lucide-react';
import emailjs from '@emailjs/browser';

const Contact = () => {
  const [status, setStatus] = useState('idle'); // idle, sending, success
  const [logs, setLogs] = useState([
    { type: 'system', text: 'Initializing secure link...' },
    { type: 'success', text: 'Connection established via Port 443' },
    { type: 'system', text: 'Waiting for user input...' }
  ]);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const addLog = (text, type = 'system') => {
    setLogs(prev => [...prev, { text, type, time: new Date().toLocaleTimeString() }]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    addLog('Initiating transmission sequence...', 'system');

    // Simulate progress
    const log1 = setTimeout(() => addLog('Encrypting data packets...', 'system'), 600);
    const log2 = setTimeout(() => addLog('Handshaking with remote server...', 'system'), 1200);

    const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID;
    const templateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
    const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

    try {
      await emailjs.send(
        serviceId,
        templateId,
        {
          from_name: formData.name,
          name: formData.name,
          reply_to: formData.email,
          email: formData.email,
          subject: formData.subject,
          message: formData.message,
          to_name: 'Karthik Gouda'
        },
        publicKey
      );

      setStatus('success');
      addLog('TRANSMISSION SUCCESSFUL', 'success');
      addLog('Acknowledgment received from Node 01', 'success');

      setFormData({
        name: '',
        email: '',
        subject: '',
        message: ''
      });
    } catch (error) {
      console.error('Email sending failed:', error);
      clearTimeout(log1);
      clearTimeout(log2);
      addLog('TRANSMISSION FAILED: ' + (error.text || 'Verification Error'), 'warning');
      setStatus('idle');
    }
  };

  return (
    <section id="contact" className="py-24 md:py-32 bg-white overflow-hidden relative">
      {/* Subtle Dot Matrix Background */}
      <div className="absolute inset-0 opacity-[0.03]"
        style={{ backgroundImage: 'radial-gradient(#0F172A 1px, transparent 1px)', backgroundSize: '32px 32px' }} />

      <div className="container mx-auto px-6 relative z-10">
        <div className="flex flex-col lg:flex-row gap-12 lg:gap-16 items-stretch">

          {/* Left: Command Diagnostics Terminal */}
          <div className="lg:w-1/2 flex flex-col justify-between">
            <div className="mb-12">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                className="flex items-center gap-4 mb-6"
              >
                <div className="h-px w-12 bg-secondary" />
                <span className="text-secondary font-black uppercase tracking-[0.4em] text-xs">Communication Node</span>
              </motion.div>

              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                className="text-5xl md:text-7xl font-display font-black text-primary mb-8 leading-none"
              >
                Establish <span className="text-secondary italic font-normal">Contact.</span>
              </motion.h2>
              <p className="text-gray-500 max-w-md leading-relaxed">
                Send a real-time secure packet directly to Karthik's mainframe or use the quick communication node channels below.
              </p>
            </div>

            {/* macOS Dark Diagnostics Terminal with Premium Glowing Highlights */}
            <motion.div
              initial={{ opacity: 0, scale: 0.98 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              className="bg-[#0b0f19] border border-gray-800 rounded-[2.5rem] p-8 md:p-10 shadow-2xl relative overflow-hidden group flex flex-col justify-between flex-1 min-h-[380px] hover:border-secondary/20 transition-all duration-500"
            >
              {/* Top ambient color bar glow */}
              <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-secondary/50 via-primary/50 to-amber-500/50 opacity-50" />

              {/* Terminal Header */}
              <div className="flex items-center justify-between mb-8 pb-4 border-b border-white/5">
                <div className="flex gap-2">
                  <div className="w-2.5 h-2.5 rounded-full bg-red-500/50" />
                  <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/50" />
                  <div className="w-2.5 h-2.5 rounded-full bg-green-500/50" />
                </div>
                <div className="flex items-center gap-2.5 text-white/20">
                  <Terminal size={12} className="text-secondary/70 animate-pulse" />
                  <span className="text-[9px] font-mono font-black uppercase tracking-widest">system.bash — contact_node</span>
                </div>
              </div>

              {/* Monospaced Log Stream */}
              <div className="flex-1 font-mono text-[12px] space-y-3.5 mb-8 max-h-[160px] overflow-y-auto scrollbar-hide text-white/60">
                {logs.map((log, i) => (
                  <div key={i} className="flex gap-3 leading-relaxed">
                    <span className="text-white/20 select-none">[{log.time || '00:00:00'}]</span>
                    <span className={`
                      ${log.type === 'system' ? 'text-white/60' : ''}
                      ${log.type === 'success' ? 'text-secondary font-bold' : ''}
                      ${log.type === 'warning' ? 'text-yellow-500' : ''}
                    `}>
                      {log.type === 'success' ? '✔ ' : '> '}{log.text}
                    </span>
                  </div>
                ))}
                {status === 'sending' && (
                  <motion.div
                    animate={{ opacity: [1, 0] }}
                    transition={{ repeat: Infinity, duration: 0.8 }}
                    className="w-1.5 h-3.5 bg-secondary inline-block ml-1"
                  />
                )}
              </div>

              {/* INTERACTIVE DATA BUFFER MIRRORING (Shows live state input inside the terminal!) */}
              <div className="mb-8 p-4 bg-white/[0.02] border border-white/5 rounded-2xl font-mono text-[11px] text-secondary/80 space-y-1 select-none">
                <div className="text-white/20 uppercase tracking-widest text-[8px] mb-2 font-bold flex items-center gap-1.5 font-sans">
                  <ShieldCheck size={10} className="text-secondary" />
                  <span>Payload Encryption Buffer</span>
                </div>
                <div className="truncate">
                  <span className="text-white/30">$</span> name_buffer = <span className="text-amber-400">"{formData.name || 'empty'}"</span>
                </div>
                <div className="truncate">
                  <span className="text-white/30">$</span> mail_buffer = <span className="text-amber-400">"{formData.email || 'empty'}"</span>
                </div>
              </div>

              {/* Quick Communication Badge Channels */}
              <div className="pt-6 border-t border-white/5">
                <div className="flex flex-wrap gap-4 items-center">
                  <a 
                    href="mailto:kartikagouda644@gmail.com" 
                    className="flex-1 min-w-[200px] flex items-center gap-4 bg-white/5 p-4 rounded-2xl border border-transparent hover:border-secondary/20 hover:bg-secondary/5 transition-all duration-300 group/email"
                  >
                    <div className="w-9 h-9 rounded-lg bg-secondary/10 flex items-center justify-center text-secondary border border-secondary/10 group-hover/email:bg-secondary group-hover/email:text-white transition-all">
                      <Mail size={16} />
                    </div>
                    <div className="truncate">
                      <span className="text-[9px] text-gray-500 block uppercase tracking-widest font-mono">Mail Node</span>
                      <span className="text-white text-xs font-bold font-mono">kartikagouda644@gmail.com</span>
                    </div>
                  </a>

                  <div className="flex gap-3">
                    {[
                      { icon: Github, href: 'https://github.com/Karthikgouda15' },
                      { icon: Linkedin, href: 'https://www.linkedin.com/in/karthikgouda15/' }
                    ].map((btn, i) => (
                      <a 
                        key={i} 
                        href={btn.href} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="w-14 h-14 rounded-2xl bg-white/5 border border-white/10 hover:border-secondary/30 hover:bg-secondary/5 flex items-center justify-center text-white/40 hover:text-secondary transition-all"
                      >
                        <btn.icon size={20} />
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Right: Beautiful Glass Parameter Input Form with Premium Glow Focus */}
          <div className="lg:w-1/2">
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              className="bg-slate-50 border border-gray-100 rounded-[3rem] p-8 md:p-12 shadow-sm relative h-full flex flex-col justify-center overflow-hidden"
            >
              <AnimatePresence mode="wait">
                {status === 'success' ? (
                  <motion.div
                    key="success"
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 1.05 }}
                    className="text-center py-12 space-y-6"
                  >
                    <div className="w-16 h-16 bg-secondary/10 rounded-full flex items-center justify-center mx-auto text-secondary">
                      <CheckCircle2 size={32} />
                    </div>
                    <div>
                      <h3 className="text-3xl font-display font-black text-primary mb-2 italic">Transmission Sent</h3>
                      <p className="text-gray-500 max-w-xs mx-auto text-sm">Your message has been encoded and successfully delivered. Karthik will respond shortly.</p>
                    </div>
                    <button
                      onClick={() => setStatus('idle')}
                      className="px-8 py-3.5 bg-primary text-white rounded-xl text-xs font-black uppercase tracking-widest hover:bg-secondary transition-all"
                    >
                      New Transmission
                    </button>
                  </motion.div>
                ) : (
                  <motion.form
                    key="form"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onSubmit={handleSubmit}
                    className="space-y-6"
                  >
                    <div className="mb-6">
                      <h3 className="text-3xl font-display font-black text-primary mb-2 italic">Initiate Session</h3>
                      <p className="text-gray-500 text-sm">Fill in the deployment parameters below.</p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      {/* Name Field */}
                      <div className="space-y-2">
                        <label className="text-[9px] font-black uppercase tracking-widest text-gray-400 ml-1">Identifier</label>
                        <input
                          required
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          placeholder="Your Name"
                          className="w-full bg-white border border-gray-200 rounded-2xl px-6 py-4.5 focus:border-secondary focus:shadow-[0_0_15px_rgba(20,184,166,0.12)] transition-all duration-300 outline-none text-primary font-medium"
                        />
                      </div>
                      
                      {/* Email Field */}
                      <div className="space-y-2">
                        <label className="text-[9px] font-black uppercase tracking-widest text-gray-400 ml-1">Return Path</label>
                        <input
                          required
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          placeholder="Email Address"
                          className="w-full bg-white border border-gray-200 rounded-2xl px-6 py-4.5 focus:border-secondary focus:shadow-[0_0_15px_rgba(20,184,166,0.12)] transition-all duration-300 outline-none text-primary font-medium"
                        />
                      </div>
                    </div>

                    {/* Subject Field */}
                    <div className="space-y-2">
                      <label className="text-[9px] font-black uppercase tracking-widest text-gray-400 ml-1">Transmission Subject</label>
                      <input
                        required
                        type="text"
                        name="subject"
                        value={formData.subject}
                        onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                        placeholder="Project Title"
                        className="w-full bg-white border border-gray-200 rounded-2xl px-6 py-4.5 focus:border-secondary focus:shadow-[0_0_15px_rgba(20,184,166,0.12)] transition-all duration-300 outline-none text-primary font-medium"
                      />
                    </div>

                    {/* Message Field */}
                    <div className="space-y-2">
                      <label className="text-[9px] font-black uppercase tracking-widest text-gray-400 ml-1">Core Message</label>
                      <textarea
                        required
                        rows="4"
                        name="message"
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        placeholder="Describe the architectural requirements..."
                        className="w-full bg-white border border-gray-200 rounded-2xl px-6 py-4.5 focus:border-secondary focus:shadow-[0_0_15px_rgba(20,184,166,0.12)] transition-all duration-300 outline-none text-primary font-medium resize-none"
                      />
                    </div>

                    {/* Send Button with Glow and Slide Icon Effect */}
                    <button
                      disabled={status === 'sending'}
                      className="w-full group flex items-center justify-center gap-3 py-5 bg-primary hover:bg-secondary text-white rounded-2xl text-xs font-black uppercase tracking-[0.2em] shadow-lg hover:shadow-secondary/25 hover:shadow-2xl transition-all duration-300 disabled:opacity-50"
                    >
                      {status === 'sending' ? 'Encrypting...' : 'Execute Transmission'}
                      <Send size={16} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                    </button>
                  </motion.form>
                )}
              </AnimatePresence>

              {/* Status Badge with Float Animation */}
              <motion.div 
                animate={{ y: [0, -4, 0] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                className="absolute -top-4 -right-4 bg-secondary text-white px-4 py-2 rounded-xl text-[9px] font-black uppercase tracking-widest shadow-xl flex items-center gap-2"
              >
                <div className={`w-2 h-2 rounded-full bg-white ${status === 'sending' ? 'animate-ping' : ''}`} />
                {status === 'sending' ? 'Transmitting' : 'Node Online'}
              </motion.div>
            </motion.div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default Contact;